import { TaskGroupIdentifier } from '@bariendo/lib/constants';
import {
  ATTACHMENT_FRAGMENT,
  CODEABLE_CONCEPT_FRAGMENT,
  EXTENSION_FRAGMENT,
  META_FRAGMENT,
} from '../../../services/graphql';
import {
  DOCUMENT_REFERENCE_FRAGMENT,
  PART_OF_FRAGMENT,
  SERVICE_REQUEST_LAB_ORDER_TIMELINE_FRAGMENT,
  TimelineCommunicationResource,
} from '../../PatientTimelinePage/graphql';

// Re-export types for convenience
export type { TimelineCommunicationResource };

// Organization and Practitioner fragment with aliased name field to avoid conflicts
// Using organizationName and practitionerName instead of just name
const PARTNER_ORGANIZATION_FRAGMENT = `
  fragment PartnerOrganizationFragment on Organization {
    id
    resourceType
    organizationName: name
  }
`;

const PARTNER_PRACTITIONER_FRAGMENT = `
  fragment PartnerPractitionerFragment on Practitioner {
    id
    resourceType
    practitionerName: name {
      given
      family
    }
    photo {
      url
      contentType
    }
  }
`;

// Simplified AssigneeTaskFragment for partners (no QuestionnaireResponse needed)
const ASSIGNEE_TASK_FRAGMENT = `
  fragment AssigneeTaskFragment on Task {
    id
    resourceType
    status
    priority
    focus {
      display
      reference
    }
    owner {
      reference
      display
      resource {
        ... on Practitioner {
          ...PartnerPractitionerFragment
        }
      }
    }
    executionPeriod {
      start
      end
    }
  }
`;

/**
 * Common Communication fields fragment for Partner Timeline queries
 * This is shared between sender and recipient queries to avoid duplication
 */
const PARTNER_COMMUNICATION_FIELDS = `
  id
  resourceType
  status
  sent
  received
  priority
  # A second Twilio SID is merged on when a provider's leg connects — one of the
  # few signals that a call was actually picked up, and one deriveVoiceCallOutcome
  # reads for the row icon as well as the call badge.
  identifier {
    system
    value
  }
  basedOn {
    reference
    display
    resource {
      ... on ServiceRequest {
        ...ServiceRequestLabOrderTimelineFragment
      }
    }
  }
  partOf {
    reference
    display
    resource {
      ...PartOfFragment
    }
  }
  category {
    ...CodeableConceptFragment
  }
  medium {
    ...CodeableConceptFragment
  }
  subject {
    reference
    display
    resource {
      ... on Organization {
        ...PartnerOrganizationFragment
      }
    }
  }
  sender {
    reference
    display
    resource {
      ... on Organization {
        ...PartnerOrganizationFragment
      }
      ... on Practitioner {
        ...PartnerPractitionerFragment
      }
    }
  }
  recipient {
    reference
    display
    resource {
      ... on Organization {
        ...PartnerOrganizationFragment
      }
      ... on Practitioner {
        ...PartnerPractitionerFragment
      }
    }
  }
  payload {
    id
    contentString
    contentAttachment {
      ...AttachmentFragment
    }
    contentReference {
      reference
      resource {
        ...DocumentReferenceFragment
      }
    }
    extension {
      url
      valueDateTime
    }
  }
  topic {
    ...CodeableConceptFragment
  }
  extension {
    ...ExtensionFragment
  }
  meta {
    ...MetaFragment
  }
  assigneeTask: TaskList(_reference: focus, group_identifier: "${TaskGroupIdentifier.RESOURCE_ASSIGNMENT}") {
    ...AssigneeTaskFragment
  }
`;

/**
 * Partner Timeline GraphQL Query
 * Queries Communications where the Organization is either:
 * - The sender of the communication
 * - A recipient of the communication
 *
 * Note: Since Medplum GraphQL doesn't support OR queries directly, we use separate queries
 * for sender and recipient, then combine the results.
 */

export const PARTNER_TIMELINE_QUERY = `
  ${CODEABLE_CONCEPT_FRAGMENT}
  ${EXTENSION_FRAGMENT}
  ${ATTACHMENT_FRAGMENT}
  ${DOCUMENT_REFERENCE_FRAGMENT}
  ${PART_OF_FRAGMENT}
  ${META_FRAGMENT}
  ${PARTNER_ORGANIZATION_FRAGMENT}
  ${PARTNER_PRACTITIONER_FRAGMENT}
  ${ASSIGNEE_TASK_FRAGMENT}
  ${SERVICE_REQUEST_LAB_ORDER_TIMELINE_FRAGMENT}

  query ($categories: String, $organizationRef: String) {
    CommunicationList(category: $categories, sender: $organizationRef, _sort: "-sent") {
      ${PARTNER_COMMUNICATION_FIELDS}
    }
  }
`;

/**
 * Query for Communications where organization is a recipient
 * This is needed because we can't do OR queries in Medplum GraphQL
 */
export const PARTNER_TIMELINE_RECIPIENT_QUERY = `
  ${CODEABLE_CONCEPT_FRAGMENT}
  ${EXTENSION_FRAGMENT}
  ${ATTACHMENT_FRAGMENT}
  ${DOCUMENT_REFERENCE_FRAGMENT}
  ${PART_OF_FRAGMENT}
  ${META_FRAGMENT}
  ${PARTNER_ORGANIZATION_FRAGMENT}
  ${PARTNER_PRACTITIONER_FRAGMENT}
  ${ASSIGNEE_TASK_FRAGMENT}
  ${SERVICE_REQUEST_LAB_ORDER_TIMELINE_FRAGMENT}

  query ($categories: String, $organizationRef: String) {
    CommunicationList(category: $categories, recipient: $organizationRef, _sort: "-sent") {
      ${PARTNER_COMMUNICATION_FIELDS}
    }
  }
`;
